import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Fetch all lab tests
export const fetchLabTestTypes = createAsyncThunk(
  "labTest/fetchLabTests",
  async (_, { rejectWithValue }) => {
    try {
       const res = await fetch("/api/v1/admin/lab_test_types", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch lab tests");
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Create lab test
export const createLabTest = createAsyncThunk(
  "labTest/createLabTest",
  async (data, { rejectWithValue }) => {
    try {
      const res = await fetch("/api/v1/admin/lab_test_types", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lab_test_type: data }),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.errors?.join(", ") || "Failed to create test");

      return result;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Update lab test
export const updateLabTestAsync = createAsyncThunk(
  "labTest/updateLabTest",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await fetch(`/api/v1/admin/lab_test_types/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lab_test_type: data }),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.errors?.join(", ") || "Failed to update test");

      return result;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Delete lab test
export const deleteLabTestAsync = createAsyncThunk(
  "labTest/deleteLabTest",
  async (id, { rejectWithValue }) => {
    try {
      const res = await fetch(`/api/v1/admin/lab_test_types/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.errors?.join(", ") || "Failed to delete test");
      }

      return id;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const labTestSlice = createSlice({
  name: "labServices",
  initialState: {
    labTests: [],
    loading: false,
    submitting: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchLabTestTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLabTestTypes.fulfilled, (state, action) => {
        state.labTests = action.payload;
        state.loading = false;
      })
      .addCase(fetchLabTestTypes.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })

      // Create
      .addCase(createLabTest.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createLabTest.fulfilled, (state, action) => {
        state.submitting = false;
        state.labTests.push(action.payload);
      })
      .addCase(createLabTest.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateLabTestAsync.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateLabTestAsync.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.labTests.findIndex((t) => t.id === action.payload.id);
        if (index >= 0) state.labTests[index] = action.payload;
      })
      .addCase(updateLabTestAsync.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteLabTestAsync.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(deleteLabTestAsync.fulfilled, (state, action) => {
        state.submitting = false;
        state.labTests = state.labTests.filter((t) => t.id !== action.payload);
      })
      .addCase(deleteLabTestAsync.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      });
  },
});

export const selectLabTestTypes = (state) => state.labServices.labTests;
export const labTestLoading = (state) => state.labServices.loading;

export default labTestSlice.reducer;
